import { csrfToken } from "utils/csrf-token"

import {
  SECTION_KINDS,
  SearchResponseItem,
  SearchResult,
  SearchSectionKind,
  SearchSection,
  SearchItem,
  SnowCountry,
  MountainCountry,
  SnowLocation,
  MountainLocation,
  WeatherLocation,
  SurfLocation,
  TideLocation,
  TideCountry,
  TideCity,
  MountainCity,
  SnowCity,
  SurfCountry,
  SurfRegion,
  SurfCity,
  SurfProvince,
  WeatherCountry,
} from "./data"

export type SearchOptions = {
  kind?: string
  country?: string
}

type SearchResponseSection = {
  title?: string
  items: SearchResponseItem[]
}

type SearchResponse = {
  main?: SearchResponseSection
  other?: SearchResponseSection
  nearby?: SearchResponseSection
}

export class LocationSearchApi {
  private cache: Map<string, SearchResult>

  constructor() {
    this.cache = new Map()
  }

  async get(rawQuery: string, options: SearchOptions): Promise<SearchResult> {
    const query = rawQuery.trim().toLowerCase()
    if (query === "") return []

    const request = JSON.stringify({ query, ...options })
    const cached = this.cache.get(request)
    if (cached !== undefined) return cached

    const response = await fetch("/location_search", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken(),
      },
      body: request,
    })
    if (!response.ok) throw new Error(`${response.status} ${response.statusText}`)

    const json: SearchResponse = await response.json()
    const result: SearchResult = []
    SECTION_KINDS.forEach((kind) => {
      const section = this.wrapSection(json, kind)
      if (section) result.push(section)
    })
    this.cache.set(request, result)
    return result
  }

  private wrapSection(
    response: SearchResponse,
    kind: SearchSectionKind,
  ): SearchSection | undefined {
    const section = response[kind]
    if (!section || section.items.length === 0) return undefined

    const items: SearchItem[] = []
    section.items.forEach((item) => {
      const wrapped = this.wrapItem(item)
      if (wrapped) items.push(wrapped)
    })

    return { kind, title: section.title, items }
  }

  // eslint-disable-next-line class-methods-use-this
  private wrapItem(item: SearchResponseItem): SearchItem | undefined {
    const klass = item.type
    switch (klass) {
      case "SnowLocation":
        return new SnowLocation(item)
      case "SnowCountry":
        return new SnowCountry(item)
      case "SnowCity":
        return new SnowCity(item)
      case "MountainLocation":
        return new MountainLocation(item)
      case "MountainCountry":
        return new MountainCountry(item)
      case "MountainCity":
        return new MountainCity(item)
      case "SurfLocation":
        return new SurfLocation(item)
      case "SurfCountry":
        return new SurfCountry(item)
      case "SurfRegion":
        return new SurfRegion(item)
      case "SurfProvince":
        return new SurfProvince(item)
      case "SurfCity":
        return new SurfCity(item)
      case "TideLocation":
        return new TideLocation(item)
      case "TideCountry":
        return new TideCountry(item)
      case "TideCity":
        return new TideCity(item)
      case "WeatherCountry":
        return new WeatherCountry(item)
      case "WeatherLocation":
      case "WeatherSmallLocation":
        return new WeatherLocation(item)
      default:
        return undefined
    }
  }
}
